import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  activateProviderProfile,
  agreeToProviderTerms,
  createProviderProfile,
  deactivateProviderProfile,
  deleteMyProviderReview,
  getMyProviderAnalytics,
  getMyProviderProfile,
  getMyProviderBusinesses,
  getProviderProfile,
  getProviderReviews,
  registerProviderProgram,
  reportProviderReview,
  saveProviderReview,
  recordProviderContact,
  requestCoachWorkspace,
  searchProviders,
  uploadProviderProfilePhoto,
  updateAcademyDetails,
  updateCoachDetails,
  updateProviderProfile,
  updateStringerDetails,
} from '../lib/pros';
import type {
  CreateProviderProfilePayload,
  ProviderAcademyDetails,
  ProviderCoachDetails,
  ProviderContactMethod,
  ProviderStringerDetails,
  ReportProviderReviewPayload,
  SaveProviderReviewPayload,
  SearchProvidersPayload,
  TenantProgramPublicRegistrationPayload,
  UpdateProviderProfilePayload,
} from '../types';
import { homeKeys } from './useHomeDashboard';

export const prosKeys = {
  all: ['pros'] as const,
  search: (params: SearchProvidersPayload) => [...prosKeys.all, 'search', params] as const,
  profile: (providerId: number) => [...prosKeys.all, 'profile', providerId] as const,
  myProfile: (id?: number) => id === undefined ? [...prosKeys.all, 'my-profile'] as const : [...prosKeys.all, 'my-profile', id] as const,
  analytics: (id?: number) => id === undefined ? [...prosKeys.all, 'analytics'] as const : [...prosKeys.all, 'analytics', id] as const,
  reviews: (providerId: number) => [...prosKeys.all, 'reviews', providerId] as const,
};

export function useProsSearch(params: SearchProvidersPayload, enabled: boolean = true) {
  return useQuery({
    queryKey: prosKeys.search(params),
    queryFn: () => searchProviders(params),
    enabled,
  });
}

export function useProsProfile(providerId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: prosKeys.profile(providerId),
    queryFn: () => getProviderProfile(providerId),
    enabled,
  });
}

export function useMyProsProfile(enabled: boolean = true, providerId?: number) {
  return useQuery({
    queryKey: prosKeys.myProfile(providerId),
    queryFn: () => getMyProviderProfile(providerId),
    enabled,
  });
}

export function useMyProsAnalytics(enabled: boolean = true, providerId?: number) {
  return useQuery({
    queryKey: prosKeys.analytics(providerId),
    queryFn: () => getMyProviderAnalytics(providerId),
    enabled,
  });
}

export function useCreateProsProfile(requestId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateProviderProfilePayload) => createProviderProfile(payload, requestId),
    onSuccess: (profile) => {
      queryClient.setQueryData(prosKeys.myProfile(requestId ? profile.id : undefined), profile);
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useUpdateProsProfile(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateProviderProfilePayload) => updateProviderProfile(payload, providerId),
    onSuccess: (profile) => {
      queryClient.setQueryData(prosKeys.myProfile(providerId), profile);
      queryClient.setQueryData(prosKeys.profile(profile.id), profile);
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useUploadProsProfilePhoto(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadProviderProfilePhoto(file, providerId),
    onSuccess: (profile) => {
      queryClient.setQueryData(prosKeys.myProfile(providerId), profile);
      queryClient.setQueryData(prosKeys.profile(profile.id), profile);
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useRequestCoachWorkspace(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestNotes?: string) => requestCoachWorkspace(requestNotes, providerId),
    onSuccess: (profile) => {
      queryClient.setQueryData(prosKeys.myProfile(providerId), profile);
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
      queryClient.invalidateQueries({ queryKey: homeKeys.all });
    },
  });
}

export function useUpdateProsStringerDetails(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProviderStringerDetails) => updateStringerDetails(payload, providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useUpdateProsCoachDetails(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProviderCoachDetails) => updateCoachDetails(payload, providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useUpdateProsAcademyDetails(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ProviderAcademyDetails) => updateAcademyDetails(payload, providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useAgreeToProviderTerms(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (termsVersion: string) => agreeToProviderTerms(termsVersion, providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.myProfile() });
    },
  });
}

export function useActivateProsProfile(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activateProviderProfile(providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useDeactivateProsProfile(providerId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deactivateProviderProfile(providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useRecordProviderContact() {
  return useMutation({
    mutationFn: ({
      providerId,
      contactMethod,
    }: {
      providerId: number;
      contactMethod: ProviderContactMethod;
    }) => recordProviderContact(providerId, contactMethod),
  });
}

export function useRegisterProviderProgram(providerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TenantProgramPublicRegistrationPayload) =>
      registerProviderProgram(providerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.profile(providerId) });
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useProsReviews(providerId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: prosKeys.reviews(providerId),
    queryFn: () => getProviderReviews(providerId),
    enabled,
  });
}

export function useSaveProsReview(providerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, update }: { payload: SaveProviderReviewPayload; update: boolean }) =>
      saveProviderReview(providerId, payload, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.reviews(providerId) });
      queryClient.invalidateQueries({ queryKey: prosKeys.profile(providerId) });
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useDeleteMyProsReview(providerId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteMyProviderReview(providerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: prosKeys.reviews(providerId) });
      queryClient.invalidateQueries({ queryKey: prosKeys.profile(providerId) });
      queryClient.invalidateQueries({ queryKey: prosKeys.all });
    },
  });
}

export function useReportProsReview(providerId: number) {
  return useMutation({
    mutationFn: ({ reviewId, payload }: { reviewId: number; payload: ReportProviderReviewPayload }) =>
      reportProviderReview(providerId, reviewId, payload),
  });
}

export function useMyProsBusinesses(enabled = true) {
  return useQuery({ queryKey: [...prosKeys.all, 'owned-businesses'], queryFn: getMyProviderBusinesses, enabled });
}
